/**
 * Structure copied from ./Payments.tsx (loader reads paging from the URL, `PagedTable` owns the
 * frame) — vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/invoice-list/table.tsx via
 * ../invoicing/List.tsx — applied to the receipts Tally posts to the Customers ledger (§17 Finance:
 * "receipts (from Tally)"). `?customerId=` narrows to one customer, reached from a customer link.
 */
import { Link, useLoaderData, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { Button } from "rizzui";
import { EmptyState, ErrorSurface } from "@steelyard/theme";
import { PagedTable } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { receiptColumns } from "./receipt-columns";
import { listReceipts } from "../../lib/services/customers";
import { hrefWith, readPaging } from "../../lib/paging";
import { can } from "../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../types";

export async function receiptsListLoader({ request }: LoaderFunctionArgs) {
  const params = new URL(request.url).searchParams;
  const { page, pageSize } = readPaging(params);
  const customerId = params.get("customerId") ?? undefined;

  return { result: await listReceipts({ page, pageSize, customerId }), customerId };
}

export default function ReceiptsList() {
  const { result, customerId } = useLoaderData() as Awaited<ReturnType<typeof receiptsListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();

  if (!can(permissionState, "customers.credit")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view receipts" />;
  }

  const filtered = Boolean(customerId);

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Receipts"
        count={result.totalCount}
        description="Receipts received from Tally and applied to customer balances, newest first."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Finance" }, { name: "Receipts" }]}
      />

      <PagedTable
        result={result}
        caption="Receipts from Tally, with voucher, customer, amount and when each was applied"
        columns={receiptColumns}
        getRowId={(row) => row.id}
        clear={{ active: filtered, href: hrefWith("/finance/receipts", searchParams, { customerId: undefined, page: undefined }) }}
        empty={
          <EmptyState
            title="No receipts yet"
            action={
              filtered ? (
                <Link to="/finance/receipts">
                  <Button as="span" size="sm" variant="outline">
                    Every customer
                  </Button>
                </Link>
              ) : null
            }
          >
            {filtered ? "Tally has sent no receipt for this customer." : "Tally has not sent any receipts yet."}
          </EmptyState>
        }
      />
    </div>
  );
}
